import React, { useState, useEffect } from 'react';
import { Typography, Breadcrumb, Card, Row, Col, Button, message, Tag, Avatar, Popconfirm, Space } from 'antd';
import { LoadingOutlined, PlusOutlined, DeleteOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;

const Integrations = () => {
  const { token, currentUser } = useAuth();
  const [loading, setLoading] = useState({});
  const MAX_ACCOUNTS = 3; // Maximum accounts per integration
  
  const [connections, setConnections] = useState({
    facebook: [], // Array of connected Facebook accounts (max 3)
    instagram: [], // Array of connected Instagram accounts (max 3)
    google: [] // Array of connected Google Ads accounts (max 3)
  });

  const loadConnections = React.useCallback(async () => {
    if (!token) return;

    // Load Facebook accounts
    try {
      const fbRes = await fetch('/api/integrations/facebook', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (fbRes.status === 401) {
        message.error('Your session has expired. Please log in again.');
        return;
      }
      if (fbRes.ok) {
        const fbData = await fbRes.json();
        const raw = Array.isArray(fbData) ? fbData : (fbData.accounts || []);
        const accounts = raw.map(acc => ({
          id: acc.id,
          account_slot: acc.account_slot,
          name: acc.account_name || acc.name || 'Facebook Account',
          email: acc.account_email || acc.email || '',
          profile_picture_url: acc.profile_picture_url || null,
        }));
        setConnections(prev => ({
          ...prev,
          facebook: accounts.slice(0, MAX_ACCOUNTS)
        }));
      }
    } catch (e) {
      console.error('Failed to load Facebook accounts:', e);
    }

    // Load Instagram accounts
    try {
      const igRes = await fetch('/api/integrations/instagram/accounts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (igRes.ok) {
        const igData = await igRes.json();
        const accounts = Array.isArray(igData) ? igData : (igData.accounts || []);
        setConnections(prev => ({
          ...prev,
          instagram: accounts.slice(0, MAX_ACCOUNTS)
        }));
      }
    } catch (e) {
      console.error('Failed to load Instagram accounts:', e);
    }

    // Load Google Ads accounts
    try {
      const googleRes = await fetch('/api/integrations/google-ads/accounts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Google Ads response status:', googleRes.status);
      if (googleRes.ok) {
        const googleData = await googleRes.json();
        console.log('Google Ads data received:', googleData);
        const accounts = Array.isArray(googleData) ? googleData : (googleData.accounts || []);
        console.log('Google Ads accounts parsed:', accounts);
        setConnections(prev => ({
          ...prev,
          google: accounts.slice(0, MAX_ACCOUNTS)
        }));
      } else {
        const errorText = await googleRes.text();
        console.error('Google Ads API error:', googleRes.status, errorText);
      }
    } catch (e) {
      console.error('Failed to load Google Ads accounts:', e);
    }
  }, [token]);

  // Load connection status on mount
  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  // Handle OAuth callback query parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const error = params.get('error');

    if (connected === 'google') {
      message.success('Google Ads connected successfully!');
      // Clear URL first, then reload connections
      window.history.replaceState({}, '', '/client/integrations');
      setTimeout(() => {
        loadConnections();
      }, 500);
    } else if (connected === 'facebook') {
      message.success('Facebook connected successfully!');
      window.history.replaceState({}, '', '/client/integrations');
      setTimeout(() => {
        loadConnections();
      }, 500);
    } else if (connected === 'instagram') {
      message.success('Instagram connected successfully!');
      window.history.replaceState({}, '', '/client/integrations');
      setTimeout(() => {
        loadConnections();
      }, 500);
    } else if (error === 'connection_failed') {
      const reason = params.get('reason');
      const detail = reason ? ` (${reason})` : '';
      message.error(`Failed to connect. Please try again.${detail}`);
      console.error('OAuth connection_failed reason:', reason);
      window.history.replaceState({}, '', '/client/integrations');
    }
  }, [loadConnections]);

  const getIntegrationName = (integrationId) => {
    const names = {
      facebook: 'Facebook',
      instagram: 'Instagram',
      google: 'Google Ads'
    };
    return names[integrationId] || integrationId;
  };

  const handleConnect = (integrationId, slotIndex) => {
    setLoading(prev => ({ ...prev, [`${integrationId}_connect_${slotIndex}`]: true }));

    // Use the same API base URL logic as the rest of the app
    const backendUrl = (() => {
      const explicit = process.env.REACT_APP_API_URL;
      if (explicit) {
        return explicit.replace(/\/api$/, ''); // Remove trailing /api if present
      }
      
      // Check if we're in production
      const hostname = window.location.hostname;
      if (hostname === 'app.pathmarkr.com' || hostname === 'pathmarkr.com' || hostname === 'www.pathmarkr.com') {
        return 'https://api.pathmarkr.com';
      }
      
      // Check if running on S3
      if (hostname.includes('s3-website') || hostname.includes('amazonaws.com')) {
        return 'https://api.pathmarkr.com';
      }
      
      // Local development
      const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
      if (isLocal) return 'http://localhost:5000';
      
      return 'https://api.pathmarkr.com';
    })();

    if (integrationId === 'facebook') {
      const fbAppId = process.env.REACT_APP_FACEBOOK_APP_ID || 'YOUR_FACEBOOK_APP_ID';
      const redirectUri = encodeURIComponent(`${backendUrl}/api/integrations/meta/callback`);
      // Only request permissions that have Advanced Access approved, so real
      // (non-tester) clients can connect. business_management and pages_manage_ads
      // were removed — they are not used by the backend and were blocking connects.
      const scope = encodeURIComponent('public_profile,ads_management,ads_read,pages_read_engagement');
      const state = encodeURIComponent(JSON.stringify({ userId: currentUser?.id, timestamp: Date.now(), slotIndex: slotIndex + 1 }));
      const fbLoginUrl = `https://www.facebook.com/v20.0/dialog/oauth?client_id=${fbAppId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&response_type=code`;
      window.location.href = fbLoginUrl;
    } else if (integrationId === 'instagram') {
      // Instagram uses Facebook OAuth
      const fbAppId = process.env.REACT_APP_FACEBOOK_APP_ID || 'YOUR_FACEBOOK_APP_ID';
      const redirectUri = encodeURIComponent(`${backendUrl}/api/integrations/instagram/callback`);
      const scope = encodeURIComponent('instagram_basic,instagram_manage_insights,pages_show_list,pages_read_engagement');
      const state = encodeURIComponent(JSON.stringify({ userId: currentUser?.id, timestamp: Date.now(), slotIndex: slotIndex + 1 }));
      const fbLoginUrl = `https://www.facebook.com/v20.0/dialog/oauth?client_id=${fbAppId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&response_type=code`;
      window.location.href = fbLoginUrl;
    } else if (integrationId === 'google') {
      const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID';
      const redirectUri = encodeURIComponent(`${backendUrl}/api/integrations/google-ads/callback`);
      const scope = encodeURIComponent('openid email profile https://www.googleapis.com/auth/adwords');
      const state = encodeURIComponent(JSON.stringify({ userId: currentUser?.id, timestamp: Date.now(), slotIndex: slotIndex + 1 }));
      const googleLoginUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&response_type=code&access_type=offline&prompt=consent`;
      window.location.href = googleLoginUrl;
    }
  };

  const handleDisconnect = async (integrationId, accountId) => {
    setLoading(prev => ({ ...prev, [`${integrationId}_${accountId}`]: true }));

    try {
      const endpoints = {
        facebook: '/api/integrations/facebook/disconnect',
        instagram: '/api/integrations/instagram/disconnect',
        google: '/api/integrations/google-ads/disconnect'
      };

      const res = await fetch(endpoints[integrationId], {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ accountId })
      });

      if (!res.ok) throw new Error('Failed to disconnect');

      // Remove account from connections
      setConnections(prev => ({
        ...prev,
        [integrationId]: prev[integrationId].filter(acc => acc.id !== accountId)
      }));

      message.success(`${getIntegrationName(integrationId)} account disconnected successfully`);
    } catch (error) {
      message.error(`Failed to disconnect ${getIntegrationName(integrationId)} account`);
    } finally {
      setLoading(prev => ({ ...prev, [`${integrationId}_${accountId}`]: false }));
    }
  };

  const integrations = [
    {
      id: 'facebook',
      name: 'Facebook Ads Manager',
      description: 'Connect your Facebook Business Manager accounts to create and manage ads directly from PathMarkr.',
      icon: (
        <svg width="36" height="36" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
          <path fill="#1877f2" d="M24,4C12.954,4,4,12.954,4,24s8.954,20,20,20s20-8.954,20-20S35.046,4,24,4z"/>
          <path fill="#fff" d="M26.707,29.301h5.176l0.813-5.258h-5.989v-2.874c0-2.184,0.714-4.121,2.757-4.121h3.283V12.46 c-0.577-0.078-1.797-0.248-4.102-0.248c-4.814,0-7.636,2.542-7.636,8.334v3.498H16.06v5.258h4.948v14.452 C21.988,43.9,22.981,44,24,44c0.921,0,1.82-0.084,2.707-0.204V29.301z"/>
        </svg>
      ),
      color: '#1877f2'
    },
    {
      id: 'instagram',
      name: 'Instagram Accounts',
      description: 'Connect your Instagram Business accounts to run Instagram ads.',
      icon: <img src="/ig.jpg" alt="Instagram" style={{ width: 36, height: 36, borderRadius: '6px' }} />,
      color: '#E4405F'
    },
    {
      id: 'google',
      name: 'Google Ads',
      description: 'Integrate with Google Ads to manage your search and display campaigns.',
      icon: (
        <svg width="36" height="36" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
          <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
          <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
          <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
          <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
        </svg>
      ),
      color: '#4285f4'
    }
  ];

  // Render account slot (connected or empty)
  const renderAccountSlot = (integration, slotIndex) => {
    const accounts = connections[integration.id] || [];
    const account = accounts[slotIndex];
    const isConnected = !!account;
    const isLoading = loading[`${integration.id}_connect_${slotIndex}`];

    if (isConnected) {
      // Display connected account
      return (
        <div
          key={`${integration.id}-slot-${slotIndex}`}
          style={{
            padding: '16px',
            background: '#f6ffed',
            border: '2px solid #b7eb8f',
            borderRadius: '8px',
            marginBottom: '12px'
          }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Space>
                <Avatar 
                  size={40}
                  src={account.profile_picture_url} 
                  icon={<UserOutlined />}
                  style={{ background: integration.color }}
                />
                <div>
                  <div>
                    <Text strong style={{ fontSize: '14px' }}>
                      {account.name || account.username || 'Account'}
                    </Text>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {account.email || account.id}
                    </Text>
                  </div>
                </div>
              </Space>
              <Tag icon={<CheckCircleOutlined />} color="success">
                Connected
              </Tag>
            </div>
            
            <Popconfirm
              title="Disconnect this account?"
              description="You can reconnect it later."
              onConfirm={() => handleDisconnect(integration.id, account.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button 
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={loading[`${integration.id}_${account.id}`]}
                block
              >
                Disconnect
              </Button>
            </Popconfirm>
          </Space>
        </div>
      );
    } else {
      // Empty slot with connect button
      return (
        <div
          key={`${integration.id}-slot-${slotIndex}`}
          style={{
            padding: '16px',
            background: '#fafafa',
            border: '2px dashed #d9d9d9',
            borderRadius: '8px',
            marginBottom: '12px',
            textAlign: 'center'
          }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Space>
                <Avatar 
                  size={40}
                  icon={<UserOutlined />}
                  style={{ background: '#d9d9d9' }}
                />
                <div style={{ textAlign: 'left' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: '14px' }}>
                      Account Slot {slotIndex + 1}
                    </Text>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Not connected
                    </Text>
                  </div>
                </div>
              </Space>
              <Tag icon={<CloseCircleOutlined />} color="default">
                Empty
              </Tag>
            </div>
            
            <Button 
              type="primary"
              size="small"
              icon={isLoading ? <LoadingOutlined /> : <PlusOutlined />}
              onClick={() => handleConnect(integration.id, slotIndex)}
              loading={isLoading}
              block
              style={{ 
                background: integration.color, 
                borderColor: integration.color 
              }}
            >
              Connect Account
            </Button>
          </Space>
        </div>
      );
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Integrations' }]} />
      
      <div style={{ marginTop: 12, marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Connected Accounts</Title>
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          Connect your advertising platforms to manage campaigns. You can connect up to {MAX_ACCOUNTS} accounts per platform.
        </Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        {integrations.map((integration) => {
          const accounts = connections[integration.id] || [];
          
          return (
            <Col xs={24} lg={8} key={integration.id}>
              <Card
                style={{ 
                  height: '100%',
                  borderTop: `4px solid ${integration.color}`
                }}
              >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ 
                    padding: '12px',
                    background: `${integration.color}15`,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {integration.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <Title level={5} style={{ margin: 0 }}>{integration.name}</Title>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {accounts.length} of {MAX_ACCOUNTS} connected
                    </Text>
                  </div>
                </div>

                <Paragraph type="secondary" style={{ fontSize: '13px', marginBottom: '20px' }}>
                  {integration.description}
                </Paragraph>

                {/* Account Slots */}
                <div>
                  {[0, 1, 2].map(slotIndex => renderAccountSlot(integration, slotIndex))}
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Info Section */}
      <Card style={{ marginTop: 24, background: '#f6f8fa' }}>
        <Title level={5}>Need Help?</Title>
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Integrations allow you to connect your advertising accounts and manage everything from one dashboard. 
          Each integration requires authentication with the respective platform.
        </Paragraph>
        <Paragraph type="secondary" style={{ margin: 0 }}>
          Your credentials are securely stored and encrypted. You can disconnect any integration at any time.
        </Paragraph>
      </Card>
    </div>
  );
};

export default Integrations;